import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n";
import { Section, SectionHeading } from "./Section";
import { TIMELINE, IMAGES } from "../data/structure";

// Era panels reuse existing translated data (no new translation keys).
const ERAS = [
  { cat: "earlyLife", rep: "birth-1969", img: IMAGES.skyline },
  { cat: "dance", rep: "dance-1980s", img: IMAGES.lightTrails },
  { cat: "film", rep: "selena-1997", img: IMAGES.redCarpet },
  { cat: "music", rep: "jlo-album-2001", img: IMAGES.stageLights },
  { cat: "fashion", rep: "this-is-me-then-2002", img: IMAGES.goldStreaks },
  { cat: "business", rep: "brand-fragrance-2005", img: IMAGES.heroGold },
  { cat: "legacy", rep: "super-bowl-2020", img: IMAGES.stageLights },
  { cat: "music", rep: "this-is-me-now-2024", img: IMAGES.goldStreaks },
];

const yearOf = (id) => (TIMELINE.find((t) => t.id === id) || {}).year;

function EraPanel({ era, index }) {
  const { d } = useI18n();
  const t = d.timeline[era.rep] || {};
  const reversed = index % 2 === 1;

  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`era-panel-${era.rep}`}
      className={`grid items-center gap-8 lg:gap-16 lg:grid-cols-2 ${reversed ? "lg:[direction:rtl]" : ""}`}
    >
      <div className="lg:[direction:ltr]">
        <div className="relative overflow-hidden rounded-3xl border border-jlo-gold/20 aspect-[4/3] group">
          <img
            src={era.img}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-[1200ms]"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-jlo-bg via-jlo-bg/40 to-transparent" />
          <span className="absolute bottom-5 left-6 font-display text-6xl sm:text-7xl font-black text-jlo-warm/15">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
      </div>

      <div className="lg:[direction:ltr]">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-jlo-gold" />
          <span className="text-xs uppercase tracking-[0.25em] text-jlo-gold font-bold">
            {d.categories[era.cat]}
          </span>
          {yearOf(era.rep) && <span className="text-sm text-jlo-gray">· {yearOf(era.rep)}</span>}
        </div>
        <h3 className="font-display text-3xl sm:text-4xl font-bold text-jlo-warm leading-tight">{t.title}</h3>
        <p className="mt-5 text-base sm:text-lg text-jlo-warm/75 leading-relaxed">{t.summary}</p>
        <p className="mt-3 text-sm text-jlo-gray leading-relaxed">{t.details}</p>
      </div>
    </motion.article>
  );
}

export default function ErasJourney() {
  const { d } = useI18n();
  return (
    <Section testid="eras-journey" className="relative">
      <SectionHeading
        kicker={d.timelineSection.kicker}
        heading={d.timelineSection.heading}
        subtitle={d.timelineSection.subtitle}
      />
      <div className="mt-16 space-y-20 sm:space-y-28">
        {ERAS.map((era, i) => (
          <EraPanel key={`${era.rep}-${i}`} era={era} index={i} />
        ))}
      </div>
    </Section>
  );
}
